



import React, { useEffect, useMemo, useState } from "react";
import "./VillaManager.css";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
).replace(/\/$/, "");

const AMENITIES = [
  "Swimming Pool", "Private Garden", "Free Wi-Fi", "Free Parking",
  "Air Conditioning", "Kitchen", "Refrigerator", "TV", "Power Backup",
  "Bonfire", "BBQ Area", "Balcony", "Mountain View", "Pet Friendly",
  "Security", "Housekeeping", "Washing Machine", "Private Terrace",
];

const STEPS = [
  { title: "Basic details", subtitle: "Tell guests about your villa" },
  { title: "Location", subtitle: "Where guests will stay" },
  { title: "Rooms & pricing", subtitle: "Capacity and nightly rate" },
  { title: "Amenities", subtitle: "Facilities and house rules" },
  { title: "Photos & review", subtitle: "Showcase your property" },
];

const MAX_GALLERY = 30;

const emptyForm = () => ({
  name: "",
  shortDescription: "",
  description: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  addressLine: "",
  city: "",
  state: "",
  country: "India",
  pincode: "",
  latitude: "",
  longitude: "",
  bedrooms: "1",
  bathrooms: "1",
  maxGuests: "2",
  pricePerNight: "",
  checkInTime: "14:00",
  checkOutTime: "11:00",
  amenities: [],
  cancellationPolicy: "Please contact the property for cancellation terms.",
  houseRules: "Guests must follow the property's house rules.",
});

const formFromVilla = (v) => ({
  name: v.name || "",
  shortDescription: v.shortDescription || "",
  description: v.description || "",
  contactName: v.contact?.name || "",
  contactPhone: v.contact?.phone || "",
  contactEmail: v.contact?.email || "",
  addressLine: v.address?.addressLine || "",
  city: v.address?.city || "",
  state: v.address?.state || "",
  country: v.address?.country || "India",
  pincode: v.address?.pincode || "",
  latitude: v.address?.location?.latitude ?? "",
  longitude: v.address?.location?.longitude ?? "",
  bedrooms: String(v.bedrooms ?? 1),
  bathrooms: String(v.bathrooms ?? 1),
  maxGuests: String(v.maxGuests ?? 2),
  pricePerNight: String(v.pricePerNight ?? ""),
  checkInTime: v.checkInTime || "14:00",
  checkOutTime: v.checkOutTime || "11:00",
  amenities: Array.isArray(v.amenities) ? v.amenities : [],
  cancellationPolicy: v.policies?.cancellationPolicy || "",
  houseRules: v.policies?.houseRules || "",
});

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("vendorToken");

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      result.message ||
        (response.status === 401
          ? "Session expired. Please log in again."
          : "Something went wrong. Please try again.")
    );
  }

  return result;
}

function Field({
  label, name, value, onChange, placeholder,
  type = "text", required = false, min, max, hint,
}) {
  return (
    <div className="villa-field">
      <label htmlFor={name}>
        {label} {required && <span className="villa-required">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        min={min}
        max={max}
      />
      {hint && <small>{hint}</small>}
    </div>
  );
}

function ActiveBadge({ active }) {
  return (
    <span
      className={`villa-status ${
        active ? "villa-status-active" : "villa-status-inactive"
      }`}
    >
      {active ? "ACTIVE" : "INACTIVE"}
    </span>
  );
}

export default function VillaManager() {
  const [villas, setVillas] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [pageError, setPageError] = useState("");
  const [notice, setNotice] = useState("");

  // new files chosen in the form
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [coverFile, setCoverFile] = useState(null);

  // already-saved images (edit mode)
  const [existingImages, setExistingImages] = useState([]);
  const [existingCover, setExistingCover] = useState("");

  const isEdit = Boolean(editingId);

  const galleryPreviews = useMemo(
    () => galleryFiles.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [galleryFiles]
  );

  useEffect(
    () => () => galleryPreviews.forEach((i) => URL.revokeObjectURL(i.url)),
    [galleryPreviews]
  );

  const coverPreview = useMemo(
    () => (coverFile ? URL.createObjectURL(coverFile) : ""),
    [coverFile]
  );

  useEffect(
    () => () => {
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    },
    [coverPreview]
  );

  const loadVillas = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const result = await apiRequest("/api/vendor/villas/my-villas");
      setVillas(result.villas || result.data || []);
    } catch (error) {
      setPageError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVillas();
  }, []);

  const resetFormState = () => {
    setForm(emptyForm());
    setGalleryFiles([]);
    setCoverFile(null);
    setExistingImages([]);
    setExistingCover("");
    setEditingId(null);
    setStep(0);
  };

  const closeForm = () => {
    resetFormState();
    setShowForm(false);
    setPageError("");
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const toggleAmenity = (amenity) => {
    setForm((previous) => ({
      ...previous,
      amenities: previous.amenities.includes(amenity)
        ? previous.amenities.filter((item) => item !== amenity)
        : [...previous.amenities, amenity],
    }));
  };

  const startNewVilla = () => {
    resetFormState();
    setPageError("");
    setNotice("");
    setShowForm(true);
  };

  const startEditVilla = (villa) => {
    resetFormState();
    setForm(formFromVilla(villa));
    setExistingImages(Array.isArray(villa.images) ? villa.images : []);
    setExistingCover(villa.coverImage || villa.images?.[0] || "");
    setEditingId(villa._id);
    setPageError("");
    setNotice("");
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalGallery = existingImages.length + galleryFiles.length;

  const validateStep = () => {
    if (step === 0) {
      if (!form.name.trim()) return "Please enter the villa name.";
      if (!form.shortDescription.trim()) return "Please enter a short description.";
      if (!form.contactName.trim()) return "Please enter the contact person's name.";
      if (!form.contactPhone.trim()) return "Please enter a contact phone number.";
      if (!/^[0-9+()\-\s]{7,20}$/.test(form.contactPhone.trim()))
        return "Please enter a valid contact phone number.";
      if (form.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail))
        return "Please enter a valid email address.";
    }

    if (step === 1) {
      if (!form.addressLine.trim()) return "Please enter the property address.";
      if (!form.city.trim()) return "Please enter the city.";
      if (!form.state.trim()) return "Please enter the state.";
      if (!form.pincode.trim()) return "Please enter the pincode.";
    }

    if (step === 2) {
      if (!form.pricePerNight || Number(form.pricePerNight) <= 0)
        return "Please enter a valid price per night.";
      if (
        Number(form.bedrooms) < 1 ||
        Number(form.bathrooms) < 1 ||
        Number(form.maxGuests) < 1
      )
        return "Bedrooms, bathrooms and guest capacity must be at least 1.";
    }

    if (step === 4) {
      if (!coverFile && !existingCover) return "Please select a cover photo.";
      if (totalGallery === 0) return "Please upload at least one gallery photo.";
      if (totalGallery > MAX_GALLERY)
        return `You can have a maximum of ${MAX_GALLERY} gallery photos.`;
    }

    return "";
  };

  const nextStep = () => {
    const error = validateStep();
    if (error) {
      setPageError(error);
      return;
    }
    setPageError("");
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const previousStep = () => {
    setPageError("");
    setStep((current) => Math.max(current - 1, 0));
  };

  const handleGalleryChange = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;

    const tooBig = picked.find((f) => f.size > 20 * 1024 * 1024);
    if (tooBig) {
      setPageError(`"${tooBig.name}" is larger than 20 MB.`);
      return;
    }

    const room = MAX_GALLERY - existingImages.length - galleryFiles.length;
    setGalleryFiles((previous) => [...previous, ...picked.slice(0, Math.max(room, 0))]);
    setPageError(
      picked.length > room ? `Only ${MAX_GALLERY} gallery photos are allowed in total.` : ""
    );
  };

  const removeExistingImage = (url) => {
    setExistingImages((previous) => previous.filter((u) => u !== url));
  };

  const buildFormData = () => {
    const data = new FormData();

    data.append("name", form.name.trim());
    data.append("propertyType", "villa");
    data.append("shortDescription", form.shortDescription.trim());
    data.append("description", form.description.trim());

    data.append(
      "contact",
      JSON.stringify({
        name: form.contactName.trim(),
        phone: form.contactPhone.trim(),
        email: form.contactEmail.trim(),
      })
    );

    data.append(
      "address",
      JSON.stringify({
        addressLine: form.addressLine.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        country: form.country.trim() || "India",
        pincode: form.pincode.trim(),
        location: {
          latitude: form.latitude === "" ? null : Number(form.latitude),
          longitude: form.longitude === "" ? null : Number(form.longitude),
        },
      })
    );

    data.append("bedrooms", form.bedrooms);
    data.append("bathrooms", form.bathrooms);
    data.append("maxGuests", form.maxGuests);
    data.append("pricePerNight", form.pricePerNight);
    data.append("amenities", JSON.stringify(form.amenities));
    data.append("checkInTime", form.checkInTime);
    data.append("checkOutTime", form.checkOutTime);

    data.append(
      "policies",
      JSON.stringify({
        cancellationPolicy: form.cancellationPolicy,
        houseRules: form.houseRules,
      })
    );

    if (isEdit) {
      data.append("existingImages", JSON.stringify(existingImages));
      if (!coverFile && existingCover) data.append("coverImage", existingCover);
    }

    if (coverFile) data.append("coverImage", coverFile);
    galleryFiles.forEach((file) => data.append("images", file));

    return data;
  };

  const submitVilla = async (event) => {
    event.preventDefault();

    // Only save from the last step
    if (step !== STEPS.length - 1) return;

    const error = validateStep();
    if (error) {
      setPageError(error);
      return;
    }

    setSaving(true);
    setPageError("");
    setNotice("");

    try {
      const result = await apiRequest(
        isEdit ? `/api/vendor/villas/${editingId}` : "/api/vendor/villas",
        { method: isEdit ? "PUT" : "POST", body: buildFormData() }
      );

      const saved = result.villa;

      // Show the card in the list immediately
      if (saved?._id) {
        setVillas((previous) =>
          isEdit
            ? previous.map((v) => (v._id === saved._id ? saved : v))
            : [saved, ...previous]
        );
      }

      setNotice(
        result.message ||
          (isEdit ? "Villa updated successfully." : "Villa added successfully.")
      );
      closeForm();
      loadVillas(true); // background sync with server
    } catch (err) {
      setPageError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (villa) => {
    setBusyId(villa._id);
    setPageError("");
    setNotice("");
    try {
      const result = await apiRequest(
        `/api/vendor/villas/${villa._id}/toggle-status`,
        { method: "PATCH" }
      );
      setVillas((previous) =>
        previous.map((v) =>
          v._id === villa._id ? result.villa || { ...v, isActive: !v.isActive } : v
        )
      );
      setNotice(result.message || "Villa status updated.");
    } catch (err) {
      setPageError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteVilla = async (villa) => {
    if (!window.confirm(`Delete "${villa.name}"? This cannot be undone.`)) return;

    setBusyId(villa._id);
    setPageError("");
    setNotice("");
    try {
      const result = await apiRequest(`/api/vendor/villas/${villa._id}`, {
        method: "DELETE",
      });
      setVillas((previous) => previous.filter((v) => v._id !== villa._id));
      setNotice(result.message || "Villa deleted successfully.");
    } catch (err) {
      setPageError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const stats = {
    total: villas.length,
    active: villas.filter((v) => v.isActive !== false).length,
    inactive: villas.filter((v) => v.isActive === false).length,
  };

  const coverShown = coverPreview || existingCover;

  return (
    <div className="villa-page">
      <div className="villa-page-header">
        <div>
          <div className="villa-eyebrow">STAY MANAGEMENT / VILLA</div>
          <h1>Manage your villas</h1>
          <p>Add, edit and manage your villa listings. Changes go live instantly.</p>
        </div>

        {!showForm && (
          <button className="villa-primary-btn" onClick={startNewVilla}>
            <span>＋</span> Add new villa
          </button>
        )}
      </div>

      {notice && (
        <div className="villa-alert villa-alert-success">
          <span>✓</span> {notice}
          <button onClick={() => setNotice("")} aria-label="Dismiss message">×</button>
        </div>
      )}

      {pageError && (
        <div className="villa-alert villa-alert-error">
          <span>!</span> {pageError}
          <button onClick={() => setPageError("")} aria-label="Dismiss error">×</button>
        </div>
      )}

      {!showForm ? (
        <>
          <div className="villa-stats-grid">
            <div className="villa-stat-card">
              <span className="villa-stat-icon">⌂</span>
              <div><small>Total villas</small><strong>{stats.total}</strong></div>
            </div>
            <div className="villa-stat-card">
              <span className="villa-stat-icon villa-icon-green">✓</span>
              <div><small>Active</small><strong>{stats.active}</strong></div>
            </div>
            <div className="villa-stat-card">
              <span className="villa-stat-icon villa-icon-amber">⏸</span>
              <div><small>Inactive</small><strong>{stats.inactive}</strong></div>
            </div>
          </div>

          <section className="villa-list-section">
            <div className="villa-list-heading">
              <div>
                <h2>Your properties</h2>
                <p>All villa listings associated with your vendor account.</p>
              </div>
              <button className="villa-outline-btn" onClick={() => loadVillas()}>
                ↻ Refresh
              </button>
            </div>

            {loading ? (
              <div className="villa-empty-state">
                <div className="villa-spinner" />
                <p>Loading your villas...</p>
              </div>
            ) : villas.length === 0 ? (
              <div className="villa-empty-state">
                <div className="villa-empty-icon">⌂</div>
                <h3>No villas added yet</h3>
                <p>Start by adding your first villa property.</p>
                <button className="villa-primary-btn" onClick={startNewVilla}>
                  ＋ Create your first villa
                </button>
              </div>
            ) : (
              <div className="villa-list">
                {villas.map((villa) => {
                  const img = villa.coverImage || villa.images?.[0];
                  const busy = busyId === villa._id;
                  return (
                    <article className="villa-list-card" key={villa._id}>
                      <div className="villa-list-image">
                        {img ? (
                          <img src={img} alt={villa.name} />
                        ) : (
                          <div className="villa-no-image">⌂</div>
                        )}
                      </div>

                      <div className="villa-list-content">
                        <div className="villa-list-title-row">
                          <h3>{villa.name}</h3>
                          <ActiveBadge active={villa.isActive !== false} />
                        </div>

                        <p className="villa-location-text">
                          <span>⌖</span>{" "}
                          {[villa.address?.city, villa.address?.state]
                            .filter(Boolean)
                            .join(", ") || "Location not added"}
                        </p>

                        <p className="villa-description-text">
                          {villa.shortDescription || "No short description added."}
                        </p>

                        <div className="villa-list-meta">
                          <span>▤ {villa.bedrooms || 0} bedrooms</span>
                          <span>♧ {villa.bathrooms || 0} bathrooms</span>
                          <span>♙ {villa.maxGuests || 0} guests</span>
                          <span>▧ {villa.images?.length || 0} photos</span>
                        </div>

                        <div className="villa-list-footer">
                          <strong>
                            ₹{Number(villa.pricePerNight || 0).toLocaleString("en-IN")}
                            <small> / night</small>
                          </strong>

                          <div className="villa-card-actions">
                            <button
                              className="villa-outline-btn villa-sm-btn"
                              disabled={busy}
                              onClick={() => startEditVilla(villa)}
                            >
                              ✎ Edit
                            </button>
                            <button
                              className="villa-outline-btn villa-sm-btn"
                              disabled={busy}
                              onClick={() => toggleActive(villa)}
                            >
                              {villa.isActive !== false ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              className="villa-delete-btn"
                              disabled={busy}
                              onClick={() => deleteVilla(villa)}
                            >
                              {busy ? "Please wait..." : "Delete"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      ) : (
        <form
          className="villa-form-layout"
          onSubmit={submitVilla}
          onKeyDown={(e) => {
            // Enter in a text input must not submit the form early
            if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault();
          }}
        >
          <div className="villa-form-top">
            <button type="button" className="villa-back-link" onClick={closeForm}>
              ← Back to villas
            </button>
            <span className="villa-step-count">
              {isEdit ? "EDITING · " : ""}STEP {step + 1} OF {STEPS.length}
            </span>
          </div>

          <div className="villa-stepper">
            {STEPS.map((item, index) => (
              <button
                type="button"
                key={item.title}
                className={`villa-step ${index === step ? "active" : ""} ${
                  index < step ? "completed" : ""
                }`}
                onClick={() => {
                  // editing: allow jumping anywhere; creating: only back
                  if (isEdit || index < step) {
                    setStep(index);
                    setPageError("");
                  }
                }}
              >
                <span className="villa-step-number">{index < step ? "✓" : index + 1}</span>
                <span className="villa-step-copy">
                  <strong>{item.title}</strong>
                  <small>{item.subtitle}</small>
                </span>
              </button>
            ))}
          </div>

          <section className="villa-form-card">
            <div className="villa-form-card-heading">
              <div>
                <span className="villa-section-kicker">
                  {isEdit ? "EDIT VILLA" : "PROPERTY SETUP"} · STEP {step + 1}
                </span>
                <h2>{STEPS[step].title}</h2>
                <p>{STEPS[step].subtitle}</p>
              </div>
              <div className="villa-progress-label">
                {Math.round(((step + 1) / STEPS.length) * 100)}% complete
              </div>
            </div>

            <div className="villa-progress-track">
              <div
                className="villa-progress-fill"
                style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              />
            </div>

            {step === 0 && (
              <div className="villa-form-body">
                <div className="villa-form-grid">
                  <Field label="Villa name" name="name" value={form.name}
                    onChange={updateField} placeholder="e.g. The Palm Luxury Villa" required />
                  <Field label="Contact person's name" name="contactName" value={form.contactName}
                    onChange={updateField} placeholder="Full name" required />
                  <Field label="Contact phone" name="contactPhone" value={form.contactPhone}
                    onChange={updateField} placeholder="+91 98765 43210" required />
                  <Field label="Contact email" name="contactEmail" value={form.contactEmail}
                    onChange={updateField} placeholder="host@example.com" type="email" />
                </div>

                <div className="villa-field villa-field-full">
                  <label htmlFor="shortDescription">
                    Short description <span className="villa-required">*</span>
                  </label>
                  <input
                    id="shortDescription"
                    name="shortDescription"
                    value={form.shortDescription}
                    onChange={updateField}
                    placeholder="A beautiful private villa for a relaxing getaway"
                    maxLength={180}
                    required
                  />
                  <small>{form.shortDescription.length}/180 characters</small>
                </div>

                <div className="villa-field villa-field-full">
                  <label htmlFor="description">Full description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={form.description}
                    onChange={updateField}
                    placeholder="Describe the property, views, nearby attractions and what makes it special..."
                    rows={5}
                  />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="villa-form-body">
                <div className="villa-form-grid">
                  <div className="villa-field villa-field-full">
                    <label htmlFor="addressLine">
                      Full property address <span className="villa-required">*</span>
                    </label>
                    <input id="addressLine" name="addressLine" value={form.addressLine}
                      onChange={updateField}
                      placeholder="House number, street, locality or landmark" required />
                  </div>

                  <Field label="City" name="city" value={form.city} onChange={updateField}
                    placeholder="e.g. Manali" required />
                  <Field label="State" name="state" value={form.state} onChange={updateField}
                    placeholder="e.g. Himachal Pradesh" required />
                  <Field label="Pincode" name="pincode" value={form.pincode} onChange={updateField}
                    placeholder="Postal code" required />
                  <Field label="Country" name="country" value={form.country} onChange={updateField}
                    placeholder="India" />
                  <Field label="Latitude (optional)" name="latitude" value={form.latitude}
                    onChange={updateField} placeholder="e.g. 32.2432" type="number" min="-90" max="90" />
                  <Field label="Longitude (optional)" name="longitude" value={form.longitude}
                    onChange={updateField} placeholder="e.g. 77.1892" type="number" min="-180" max="180" />
                </div>

                <div className="villa-info-note">
                  <span>ⓘ</span>
                  <p>Add the exact property address so guests can identify the location before booking.</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="villa-form-body">
                <div className="villa-form-grid">
                  <Field label="Bedrooms" name="bedrooms" value={form.bedrooms}
                    onChange={updateField} type="number" min="1" required />
                  <Field label="Bathrooms" name="bathrooms" value={form.bathrooms}
                    onChange={updateField} type="number" min="1" required />
                  <Field label="Maximum guests" name="maxGuests" value={form.maxGuests}
                    onChange={updateField} type="number" min="1" required />
                  <Field label="Price per night (₹)" name="pricePerNight" value={form.pricePerNight}
                    onChange={updateField} type="number" min="1" placeholder="e.g. 8500" required />
                  <Field label="Check-in time" name="checkInTime" value={form.checkInTime}
                    onChange={updateField} type="time" required />
                  <Field label="Check-out time" name="checkOutTime" value={form.checkOutTime}
                    onChange={updateField} type="time" required />
                </div>

                <div className="villa-price-preview">
                  <div>
                    <span>Base nightly rate</span>
                    <small>Taxes and extra charges may be additional.</small>
                  </div>
                  <strong>
                    ₹{Number(form.pricePerNight || 0).toLocaleString("en-IN")}
                    <small> / night</small>
                  </strong>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="villa-form-body">
                <div className="villa-subheading">
                  <h3>Property amenities</h3>
                  <p>Select all the facilities available to guests.</p>
                </div>

                <div className="villa-amenity-grid">
                  {AMENITIES.map((amenity) => (
                    <label
                      key={amenity}
                      className={`villa-amenity-option ${
                        form.amenities.includes(amenity) ? "selected" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.amenities.includes(amenity)}
                        onChange={() => toggleAmenity(amenity)}
                      />
                      <span className="villa-custom-checkbox">
                        {form.amenities.includes(amenity) ? "✓" : ""}
                      </span>
                      <span>{amenity}</span>
                    </label>
                  ))}
                </div>

                <div className="villa-form-divider" />

                <div className="villa-form-grid">
                  <div className="villa-field villa-field-full">
                    <label htmlFor="cancellationPolicy">Cancellation policy</label>
                    <textarea id="cancellationPolicy" name="cancellationPolicy"
                      value={form.cancellationPolicy} onChange={updateField} rows={3} />
                  </div>
                  <div className="villa-field villa-field-full">
                    <label htmlFor="houseRules">House rules</label>
                    <textarea id="houseRules" name="houseRules"
                      value={form.houseRules} onChange={updateField} rows={3} />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="villa-form-body">
                <div className="villa-upload-section">
                  <div className="villa-subheading">
                    <h3>Cover photo <span className="villa-required">*</span></h3>
                    <p>This will be the main image guests see in your listing.</p>
                  </div>

                  <label className="villa-upload-box villa-cover-upload">
                    {coverShown ? (
                      <img src={coverShown} alt="Cover preview" />
                    ) : (
                      <>
                        <span className="villa-upload-icon">↑</span>
                        <strong>Choose cover photo</strong>
                        <small>JPG, PNG or WEBP · Maximum 20 MB</small>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (!file) return;
                        if (file.size > 20 * 1024 * 1024) {
                          setPageError("Cover photo must be 20 MB or smaller.");
                          return;
                        }
                        setCoverFile(file);
                        setPageError("");
                      }}
                    />
                  </label>
                </div>

                <div className="villa-upload-section">
                  <div className="villa-subheading">
                    <h3>Villa gallery <span className="villa-required">*</span></h3>
                    <p>Upload up to {MAX_GALLERY} photos showing rooms, outdoor areas and facilities.</p>
                  </div>

                  <label className="villa-upload-box villa-gallery-upload">
                    <span className="villa-upload-icon">▧</span>
                    <strong>Click to upload gallery photos</strong>
                    <small>JPG, PNG or WEBP · Up to {MAX_GALLERY} photos · 20 MB each</small>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleGalleryChange}
                    />
                  </label>

                  {(existingImages.length > 0 || galleryPreviews.length > 0) && (
                    <div className="villa-photo-grid">
                      {existingImages.map((url, index) => (
                        <div className="villa-photo-card" key={url}>
                          <img src={url} alt={`Saved photo ${index + 1}`} />
                          <span className="villa-photo-badge">Saved</span>
                          <button type="button" aria-label="Remove photo"
                            onClick={() => removeExistingImage(url)}>×</button>
                        </div>
                      ))}

                      {galleryPreviews.map((item, index) => (
                        <div className="villa-photo-card" key={`${item.file.name}-${index}`}>
                          <img src={item.url} alt={`New photo ${index + 1}`} />
                          <span className="villa-photo-badge villa-photo-badge-new">New</span>
                          <button
                            type="button"
                            aria-label="Remove photo"
                            onClick={() =>
                              setGalleryFiles((previous) =>
                                previous.filter((_, i) => i !== index)
                              )
                            }
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="villa-upload-count">
                    {totalGallery} of {MAX_GALLERY} gallery photos
                  </p>
                </div>

                <div className="villa-review-card">
                  <h3>Listing summary</h3>
                  <div className="villa-review-row">
                    <span>Property</span><strong>{form.name || "—"}</strong>
                  </div>
                  <div className="villa-review-row">
                    <span>Location</span>
                    <strong>{[form.city, form.state].filter(Boolean).join(", ") || "—"}</strong>
                  </div>
                  <div className="villa-review-row">
                    <span>Capacity</span>
                    <strong>{form.bedrooms} bedrooms · {form.maxGuests} guests</strong>
                  </div>
                  <div className="villa-review-row">
                    <span>Nightly price</span>
                    <strong>₹{Number(form.pricePerNight || 0).toLocaleString("en-IN")}</strong>
                  </div>
                  <div className="villa-review-row">
                    <span>Amenities</span><strong>{form.amenities.length} selected</strong>
                  </div>
                </div>

                <div className="villa-info-note">
                  <span>ⓘ</span>
                  <p>
                    {isEdit
                      ? "Your changes will be saved and go live immediately."
                      : "Your villa will go live immediately after saving. No admin approval needed."}
                  </p>
                </div>
              </div>
            )}

            <div className="villa-form-actions">
              <button
                type="button"
                className="villa-outline-btn"
                onClick={step === 0 ? closeForm : previousStep}
                disabled={saving}
              >
                {step === 0 ? "Cancel" : "← Previous"}
              </button>

              {step < STEPS.length - 1 ? (
                <button type="button" className="villa-primary-btn" onClick={nextStep}>
                  Continue <span>→</span>
                </button>
              ) : (
                <button type="submit" className="villa-primary-btn" disabled={saving}>
                  {saving
                    ? isEdit ? "Updating villa..." : "Saving villa..."
                    : isEdit ? "Update villa" : "Save villa listing"}
                  {!saving && <span>✓</span>}
                </button>
              )}
            </div>
          </section>

          <p className="villa-form-footnote">
            Required fields are marked with <span className="villa-required">*</span>.
          </p>
        </form>
      )}
    </div>
  );
}